import { useState, useEffect, useRef, useCallback } from "react";
import api from "../../api";
import "../../CSS/DrsUpdation.css";
import {useRights} from "../RightsContext";

  const DrsUpd_PATH = "/DrsUpdation"; 

// ── HELPERS ────────────────────────────────────────────────────
const fmt = (n) =>
  (Math.round((n || 0) * 100) / 100)
    .toFixed(2)
    .replace(/\.00$/, "")
    .replace(/\B(?=(\d{3})+(?!\d))/g, ",");

// ── TOAST HOOK ─────────────────────────────────────────────────
function useToast() {
  const [toasts, setToasts] = useState([]);
  const toast = useCallback((msg, type = "info") => {
    const icons = { ok: "✓", err: "✕", warn: "⚠", info: "ℹ" };
    const id = Date.now() + Math.random();
    setToasts((p) => [...p, { id, msg, type, icon: icons[type] || "ℹ" }]);
    setTimeout(
      () => setToasts((p) => p.filter((t) => t.id !== id)),
      2800
    );
  }, []);
  return { toasts, toast };
}

function ToastWrap({ toasts }) {
  return (
    <div className="dru-toast-wrap">
      {toasts.map((t) => (
        <div key={t.id} className={`dru-toast ${t.type}`}>
          <span style={{ fontSize: 15 }}>{t.icon}</span>
          {t.msg}
        </div>
      ))}
    </div>
  );
}

// ── SVG ICONS ──────────────────────────────────────────────────
const IconClipboard = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M16 4h2a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V6a2 2 0 012-2h2" />
    <rect x="8" y="2" width="8" height="4" rx="1" />
  </svg>
);
const IconClock = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);
const IconScan = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 7V5a2 2 0 012-2h2M17 3h2a2 2 0 012 2v2M21 17v2a2 2 0 01-2 2h-2M7 21H5a2 2 0 01-2-2v-2" />
    <line x1="7" y1="12" x2="17" y2="12" />
  </svg>
);
const IconCheck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M9 11l3 3L22 4" />
    <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
  </svg>
);
const IconBack = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 12H5M12 5l-7 7 7 7" />
  </svg>
);
const IconPhone = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.13.96.36 1.9.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0122 16.92z" />
  </svg>
);
const IconSend = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
  </svg>
);
const IconCamera = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);
const IconImage = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);
const IconPen = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 19l7-7 3 3-7 7-3-3z" />
    <path d="M18 13l-1.5-7.5L2 2l3.5 14.5L13 18l5-5z" />
    <circle cx="11" cy="11" r="2" />
  </svg>
);
const IconTrash = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" />
  </svg>
);
const IconUser = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);
const IconLock = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="11" width="18" height="11" rx="2" />
    <path d="M7 11V7a5 5 0 0110 0v4" />
  </svg>
);
const IconFlip = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M23 4v6h-6M1 20v-6h6" />
    <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
  </svg>
);
const IconX = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
const IconMsg = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
  </svg>
);
const IconWa = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />
  </svg>
);
const IconArrow = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);

// ── STATUS CONFIG ──────────────────────────────────────────────
const STATUS_CONFIG = {
  delivered: { label: "Delivered",  icon: "✅", cls: "s-delivered" },
  partial:   { label: "Partial",    icon: "📦", cls: "s-partial" },
  attempted: { label: "Attempted",  icon: "⏱️", cls: "s-attempted" },
  returned:  { label: "Returned",   icon: "↩️", cls: "s-returned" },
};

const RECEIVED_AT_OPTIONS = [
  "Self", "Family Member", "Security / Guard",
  "Reception", "Neighbour", "Colleague", "Other",
];

// ── MAIN COMPONENT ─────────────────────────────────────────────
export default function DrsUpdation() {
  const rights = useRights(DrsUpd_PATH);
  // ── MASTERS ──
  const [boys,    setBoys]    = useState([]);
  const [curBoy,  setCurBoy]  = useState("");

  // ── VIEW ──
  const [view,    setView]    = useState("list"); // "list" | "update"
  const [curTab,  setCurTab]  = useState("pending");

  // ── CONSIGNMENTS ──
  const [consignments, setConsignments] = useState([]);

  // ── DIRECT LOOKUP ──
  const [directCnr,     setDirectCnr]     = useState("");
  const [directResult,  setDirectResult]  = useState(null);  // {found, data, alreadyDone}

  // ── UPDATE VIEW ──
  const [curRecord,   setCurRecord]   = useState(null);
  const [isDirect,    setIsDirect]    = useState(false);
  const [curStatus,   setCurStatus]   = useState("");
  const [rcvName,     setRcvName]     = useState("");
  const [rcvAt,       setRcvAt]       = useState("");
  const [rcvRemark,   setRcvRemark]   = useState("");
  const [images,      setImages]      = useState([]);  // [{url, name, file}]

  // ── OTP ──
  const [otpSent,     setOtpSent]     = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpValue,    setOtpValue]    = useState("");
  const [otpDigits,   setOtpDigits]   = useState(["","","",""]);
  const [otpTimer,    setOtpTimer]    = useState(0);
  const [otpResend,   setOtpResend]   = useState(false);
  const otpTimerRef = useRef(null);
  const otpRefs     = [useRef(null), useRef(null), useRef(null), useRef(null)];
  const [isSubmitting, setIsSubmitting] = useState(false);
 // ── OTP — use backend ──────────────────────────────────────────
  const [otpId, setOtpId] = useState(null); 

  // ── SIGNATURE ──
  const sigCanvasRef = useRef(null);
  const sigCtxRef    = useRef(null);
  const sigDrawing   = useRef(false);
  const [sigHasInk,  setSigHasInk]   = useState(false);

  // ── CAMERA ──
  const [camOpen,    setCamOpen]    = useState(false);
  const [camFacing,  setCamFacing]  = useState("environment");
  const [camError,   setCamError]   = useState(false);
  const camStreamRef  = useRef(null);
  const camVideoRef   = useRef(null);
  const camCanvasRef  = useRef(null);
  const nativeCamRef  = useRef(null);

  const directCnrRef = useRef(null);
  const { toasts, toast } = useToast();

  const [otpPhone, setOtpPhone] = useState("");

  // ── INIT ──────────────────────────────────────────────────────
  useEffect(() => {
    loadBoys();
  }, []);

  const loadBoys = async () => {
    try {
      const res = await api.get("/api/drs/delivery-boys");
      const list = res.data || [];
      setBoys(list);
      if (list.length) setCurBoy(String(list[0].id || list[0].boyId));
    } catch {
      setBoys([]);
    }
  };

  // ── LOAD CONSIGNMENTS when curBoy changes ─────────────────────
  useEffect(() => {
    if (curBoy) loadConsignments();
  }, [curBoy]);

  const loadConsignments = async () => {
    try {
      const res = await api.get(`/api/drs/consignments?boyId=${curBoy}`);
      setConsignments(res.data || []);
    } catch {
      setConsignments([]);
    }
  };

  // ── STATS ─────────────────────────────────────────────────────
  const mine    = consignments.filter((c) => String(c.boyId) === String(curBoy));
  const pending = mine.filter((c) => c.status === "PENDING"   || c.status === "pending");
  const history = mine.filter((c) => c.status !== "PENDING"   && c.status !== "pending");
  const delivered = history.filter((c) => (c.status || "").toLowerCase() === "delivered");
  const attempted = history.filter((c) => (c.status || "").toLowerCase() === "attempted");

  // ── DIRECT LOOKUP ─────────────────────────────────────────────
  // const lookupDirect = async () => {
  //   const raw = directCnr.trim().toUpperCase();
  //   if (!raw) { alert("Enter / scan a consignment number", "warn"); return; }
  //   try {
  //     const res  = await api.get(`/api/drs/fetch-cnr?cnr=${encodeURIComponent(raw)}`);
  //     const data = res.data;
  //     if (data?.success && data.booking) {
  //       const existing = mine.find(
  //         (c) => String(c.consignmentNo || c.cnr) === String(raw)
  //       );
  //       setDirectResult({ found: true, data: data.booking, existing });
  //     } else {
  //       setDirectResult({ found: false });
  //       alert(`Consignment ${raw} not found`, "err");
  //     }
  //   } catch {
  //     setDirectResult({ found: false });
  //     alert("Consignment not found", "err");
  //   }
  // };

  const lookupDirect = async () => {
  const raw = directCnr.trim().toUpperCase();
  if (!raw) { alert("Enter / scan a consignment number", "warn"); return; }
  try {
    const res  = await api.get(`/api/drs/fetch-cnr`, { params: { cnr: raw, mode: "direct" } });
    const data = res.data;
    if (data?.success && data.booking) {
      const existing = mine.find(
        (c) => String(c.consignmentNo || c.cnr) === String(raw)
      );
      setDirectResult({ found: true, data: data.booking, existing });
    } else if (data?.reason === "REQUIRES_DRS_PREPARATION") {
      // Otp_Required = 1 — this consignment must go through DRS Preparation
      setDirectResult({ found: false });
      alert(data.message || `${raw} requires OTP and must be added via DRS Preparation`, "err");
    } else {
      setDirectResult({ found: false });
      alert(`Consignment ${raw} not found`, "err");
    }
  } catch {
    setDirectResult({ found: false });
    alert("Consignment not found", "err");
  }
};
//   // ── OPEN UPDATE VIEW ──────────────────────────────────────────
//   const openUpdate = (cnr, direct = false) => {
//     // const c = mine.find((x) => String(x.consignmentNo || x.cnr) === String(cnr))
//     //        || (direct && directResult?.data);
//     const c = mine.find(
//     (x) => String(x.consignmentNo || x.cnr) === String(cnr)
// ) || (
//     direct && {
//         ...directResult?.data,
//         consignmentNo:
//             directResult?.data?.consignmentNo ||
//             directResult?.data?.consignment_No ||
//             directResult?.data?.cnr
//     }
// );
//     if (!c) return;

//     setCurRecord(c);
//     setIsDirect(direct);
//     setCurStatus("");
//     setRcvName("");
//     setRcvAt("");
//     setRcvRemark("");
//     setImages([]);
//     setOtpSent(false);
//     setOtpVerified(false);
//     setOtpValue("");
//     setOtpDigits(["","","",""]);
//     setOtpTimer(0);
//     setOtpResend(false);
//     setSigHasInk(false);
//     if (otpTimerRef.current) clearInterval(otpTimerRef.current);
//     setView("update");
//     window.scrollTo(0, 0);
//     setTimeout(initSig, 80);
//   };

// ── OPEN UPDATE VIEW ──────────────────────────────────────────
const openUpdate = async (cnr, direct = false) => {
    const c = mine.find(
      (x) => String(x.consignmentNo || x.cnr) === String(cnr)
    ) || (
      direct && {
        ...directResult?.data,
        consignmentNo:
          directResult?.data?.consignmentNo ||
          directResult?.data?.consignment_No ||
          directResult?.data?.cnr
      }
    );
    if (!c) return;

    setCurRecord(c);
    setIsDirect(direct);
    setCurStatus("");
    setRcvName("");
    setRcvAt("");
    setRcvRemark("");
    setImages([]);
    setOtpSent(false);
    setOtpVerified(false);
    setOtpValue("");
    setOtpDigits(["","","",""]);
    setOtpTimer(0);
    setOtpResend(false);
    setSigHasInk(false);
    setOtpId(null);
    if (otpTimerRef.current) clearInterval(otpTimerRef.current);

    setView("update");
    window.scrollTo(0, 0);
    setTimeout(initSig, 80);

    const cnrKey = c.consignmentNo || c.cnr;

    if (direct) {
      // Direct delivery — no preparation OTP exists for this consignment,
      // so generate + send one right now.
      await sendOtp("SMS");
      return;
    }

    // Non-direct — an OTP was already sent when this DRS was prepared.
    // Fetch it instead of sending a new one; only fall back to the
    // manual "Send OTP" button if none is found (expired / not required).
    try {
      const res = await api.get("/api/drs/otp-status", { params: { consignmentNo: cnrKey } });
      if (res.data?.exists) {
        setOtpId(res.data.otpId);
        setOtpSent(true);
        startOtpTimer();
      }
    } catch {
      // stay on manual "Send OTP" fallback
    }
  };

  const backToList = () => {
    if (otpTimerRef.current) clearInterval(otpTimerRef.current);
    stopCamStream();
    setView("list");
    window.scrollTo(0, 0);
  };

  // ── SIGNATURE ─────────────────────────────────────────────────
  const initSig = () => {
    const cv = sigCanvasRef.current;
    if (!cv) return;
    const r = cv.getBoundingClientRect();
    cv.width  = r.width  * 2;
    cv.height = r.height * 2;
    const ctx = cv.getContext("2d");
    ctx.scale(2, 2);
    ctx.lineWidth   = 2.2;
    ctx.lineCap     = "round";
    ctx.lineJoin    = "round";
    ctx.strokeStyle = "#1a2a4a";
    sigCtxRef.current = ctx;
  };

  const getSigPos = (e, cv) => {
    const r = cv.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: src.clientX - r.left, y: src.clientY - r.top };
  };

  const sigStart = (e) => {
    e.preventDefault();
    sigDrawing.current = true;
    const p = getSigPos(e, sigCanvasRef.current);
    sigCtxRef.current?.beginPath();
    sigCtxRef.current?.moveTo(p.x, p.y);
  };

  const sigMove = (e) => {
    if (!sigDrawing.current) return;
    e.preventDefault();
    const p = getSigPos(e, sigCanvasRef.current);
    sigCtxRef.current?.lineTo(p.x, p.y);
    sigCtxRef.current?.stroke();
    setSigHasInk(true);
  };

  const sigEnd = () => { sigDrawing.current = false; };

  const clearSig = () => {
    const cv = sigCanvasRef.current;
    if (cv && sigCtxRef.current) sigCtxRef.current.clearRect(0, 0, cv.width, cv.height);
    setSigHasInk(false);
  };

  useEffect(() => {
    window.addEventListener("resize", initSig);
    return () => window.removeEventListener("resize", initSig);
  }, []);

  // ── OTP ───────────────────────────────────────────────────────
  // const sendOtp = (channel) => {
  //   if (!curStatus) { toast("Select delivery status first", "warn"); return; }
  //   const otp = String(Math.floor(1000 + Math.random() * 9000));
  //   setOtpValue(otp);
  //   setOtpSent(true);
  //   setOtpVerified(false);
  //   setOtpDigits(["","","",""]);
  //   startOtpTimer();
  //   toast(`OTP sent via ${channel}: ${otp} (demo)`, "info");
  //   setTimeout(() => otpRefs[0].current?.focus(), 50);
  // };

   
const sendOtp = async (channel) => {
  if (!curStatus) { alert("Select delivery status first", "warn"); return; }
  const consignmentNo =
    curRecord?.consignmentNo ||
    curRecord?.cnr ||
    "";

  console.log("OTP Request:", {
    consignmentNo,
    boyId: curBoy,
    // mobileNo: curRecord?.mobile || curRecord?.consigneeMobile || ""
    mobileNo: otpPhone
  });
  try {
    const res = await api.post("/api/drs/send-otp", {
      // consignmentNo: consignmentNo,
      consignmentNo: String(consignmentNo),
      deliveryBoyId: curBoy,

       mobileNo: otpPhone,

      channel: "SMS",

      resendSeq: 1,
      isDirect: isDirect
    });
    setOtpId(res.data.otpId);
    setOtpSent(true);
    setOtpVerified(false);
    setOtpDigits(["","","",""]);
    startOtpTimer();
    // OTP is delivered via SMS now — don't show it on screen.
    alert(`OTP sent via ${channel}`, "info");
    setTimeout(() => otpRefs[0].current?.focus(), 50);
  } catch (err) {
    console.error("send-otp error:", err.response?.data);
    alert(err.response?.data?.message || "Failed to send OTP", "err");
  }
};

const resendOtp = async () => {
  const consignmentNo = curRecord?.consignmentNo || curRecord?.cnr || "";
  try {
    const res = await api.post("/api/drs/resend-otp", {
      consignmentNo,
      deliveryBoyId: parseInt(curBoy, 10) || null,
    });
    setOtpId(res.data.otpId);
    setOtpSent(true);
    setOtpVerified(false);
    setOtpDigits(["", "", "", ""]);
    startOtpTimer();
    alert(`OTP resent to ${res.data.mobileMasked}`, "info");
    setTimeout(() => otpRefs[0].current?.focus(), 50);
  } catch (err) {
    alert(err.response?.data?.message || "Resend failed", "err");
  }
};

  const startOtpTimer = () => {
    setOtpResend(false);
    let t = 30;
    setOtpTimer(t);
    if (otpTimerRef.current) clearInterval(otpTimerRef.current);
    otpTimerRef.current = setInterval(() => {
      t--;
      setOtpTimer(t);
      if (t <= 0) {
        clearInterval(otpTimerRef.current);
        setOtpResend(true);
      }
    }, 1000);
  };

  const handleOtpKey = (e, i) => {
    if (e.key === "Backspace" && !otpDigits[i] && i > 0) {
      otpRefs[i - 1].current?.focus();
      return;
    }
    const val = e.target.value.replace(/\D/g, "").slice(0, 1);
    const next = [...otpDigits];
    next[i] = val;
    setOtpDigits(next);
    if (val && i < 3) setTimeout(() => otpRefs[i + 1].current?.focus(), 10);
  };

  // const verifyOtp = () => {
  //   const entered = otpDigits.join("");
  //   if (entered.length < 4) { toast("Enter the 4-digit OTP", "warn"); return; }
  //   if (entered === otpValue) {
  //     setOtpVerified(true);
  //     if (otpTimerRef.current) clearInterval(otpTimerRef.current);
  //     toast("OTP verified", "ok");
  //   } else {
  //     toast("Incorrect OTP, try again", "err");
  //     setOtpDigits(["","","",""]);
  //     otpRefs[0].current?.focus();
  //   }
  // };

  const verifyOtp = async () => {
  const entered = otpDigits.join("");
  if (entered.length < 4) { alert("Enter the 4-digit OTP", "warn"); return; }
  try {
    const res = await api.post("/api/drs/verify-otp", {
      otpId,
      otp: entered,
    });
    if (res.data.success) {
      setOtpVerified(true);
      if (otpTimerRef.current) clearInterval(otpTimerRef.current);
      alert("OTP verified", "ok");
    } else {
      alert(res.data.message || "Incorrect OTP", "err");
      setOtpDigits(["","","",""]);
      otpRefs[0].current?.focus();
    }
  } catch {
    alert("OTP verification failed", "err");
  }
};

  // ── CAMERA ────────────────────────────────────────────────────
  const openCamera = () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      nativeCamRef.current?.click();
      return;
    }
    setCamOpen(true);
    setCamError(false);
    startCamStream();
  };

  const startCamStream = async (facing = camFacing) => {
    stopCamStream();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing }, audio: false,
      });
      camStreamRef.current = stream;
      if (camVideoRef.current) {
        camVideoRef.current.srcObject = stream;
        camVideoRef.current.play();
      }
      setCamError(false);
    } catch {
      setCamError(true);
    }
  };

  const stopCamStream = () => {
    if (camStreamRef.current) {
      camStreamRef.current.getTracks().forEach((t) => t.stop());
      camStreamRef.current = null;
    }
  };

  const flipCamera = () => {
    const next = camFacing === "environment" ? "user" : "environment";
    setCamFacing(next);
    startCamStream(next);
  };

  const capturePhoto = () => {
    const v  = camVideoRef.current;
    const cv = camCanvasRef.current;
    if (!camStreamRef.current || !v?.videoWidth) { alert("Camera not ready", "warn"); return; }
    cv.width  = v.videoWidth;
    cv.height = v.videoHeight;
    const ctx = cv.getContext("2d");
    if (camFacing === "user") { ctx.translate(cv.width, 0); ctx.scale(-1, 1); }
    ctx.drawImage(v, 0, 0, cv.width, cv.height);
    cv.toBlob((b) => {
      if (!b) return;
      const url  = URL.createObjectURL(b);
      const name = `POD_${Date.now()}.jpg`;
      setImages((prev) => [...prev, { url, name, file: new File([b], name, { type: "image/jpeg" }) }]);
      // alert("Photo captured", "ok");
    }, "image/jpeg", 0.9);
  };

  const closeCamera = () => { stopCamStream(); setCamOpen(false); };

  const addPhotos = (e) => {
    const files = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
    setImages((prev) => [
      ...prev,
      ...files.map((f) => ({ url: URL.createObjectURL(f), name: f.name, file: f })),
    ]);
    e.target.value = "";
    // if (files.length) alert(`${files.length} photo${files.length > 1 ? "s" : ""} added`, "ok");
  };

  const removeImage = (i) => {
    setImages((prev) => {
      URL.revokeObjectURL(prev[i].url);
      return prev.filter((_, idx) => idx !== i);
    });
  };

  // ── SUBMIT ────────────────────────────────────────────────────
  // const submitDelivery = async () => {
  //   if (!curStatus) { alert("Select a delivery status", "warn"); return; }
  //   const needsDelivery = curStatus === "delivered" || curStatus === "partial";

  //   if (needsDelivery) {
  //     if (!otpVerified)      { alert("OTP verification is mandatory for delivery", "err"); return; }
  //     if (!rcvName.trim())   { alert("Enter receiver name", "warn"); return; }
  //     if (!rcvAt)            { alert("Select Received At", "warn"); return; }
  //     if (!images.length)    { alert("Add at least one POD photo", "warn"); return; }
  //     if (!sigHasInk)        { alert("Receiver signature is required", "warn"); return; }
  //   } else {
  //     if (!rcvRemark.trim()) { alert("Enter an attempt / return remark", "warn"); return; }
  //   }

  //   try {
  //     // Build FormData for images + signature + fields
  //     const fd = new FormData();
  //     fd.append("consignmentNo",  curRecord.consignmentNo || curRecord.cnr);
  //     fd.append("boyId",          curBoy);
  //     fd.append("status",         curStatus.toUpperCase());
  //     fd.append("receiverName",   rcvName);
  //     fd.append("receivedAt",     rcvAt);
  //     fd.append("remark",         rcvRemark);
  //     fd.append("isDirect",       isDirect);

  //     // Append images
  //     images.forEach((img, i) => {
  //       if (img.file) fd.append("images", img.file, img.name);
  //     });

  //     // Append signature as blob
  //     const sigBlob = await new Promise((res) =>
  //       sigCanvasRef.current?.toBlob(res, "image/png")
  //     );
  //     if (sigBlob) fd.append("signature", sigBlob, "signature.png");

  //     await api.post("/api/drs/update-delivery", fd, {
  //       headers: { "Content-Type": "multipart/form-data" },
  //     });

  //     const labels = {
  //       delivered: "Delivered", partial: "Partially delivered",
  //       attempted: "Attempt logged", returned: "Returned",
  //     };

  //     alert(
  //       `${curRecord.consignmentNo || curRecord.cnr}${isDirect ? " (Direct)" : ""} — ${labels[curStatus]}`,
  //       "ok"
  //     );

  //     // Refresh consignments list
  //     await loadConsignments();

  //     setTimeout(() => {
  //       const nextTab = curStatus === "attempted" || curStatus === "returned" ? "pending" : "history";
  //       setCurTab(nextTab);
  //       backToList();
  //     }, 700);
  //   } catch (err) {
  //     toast(err.response?.data?.message || "Submit failed", "err");
  //   }
  // };

const getUserId = () => {
  try {
    const token   = localStorage.getItem("token");
    if (!token) return null;
    const payload = JSON.parse(atob(token.split(".")[1]));
    return payload?.UserId || payload?.userId || null;
  } catch { return null; }
};

// Best-effort geolocation for Track_Master.Latitude/Longitude and
// drs.DeliveryUpdate.GeoLat/GeoLng — resolves to { lat: null, lng: null }
// if unsupported, denied, or slow (2.5s timeout), so it never blocks the
// delivery update submission.
const getGeoLocation = () =>
  new Promise((resolve) => {
    if (!navigator.geolocation) { resolve({ lat: null, lng: null }); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve({ lat: null, lng: null }),
      { timeout: 2500, maximumAge: 60000 }
    );
  });
// ── SUBMIT — use backend ───────────────────────────────────────
const submitDelivery = async () => {
  if (isSubmitting) return;
  console.log("Delivery State:", {
    curStatus,
    otpVerified,
    rcvName,
    rcvAt,
    images,
    sigHasInk
  });
  if (!curStatus) { alert("Select a delivery status", "warn"); return; }
  const status = (curStatus || "").toLowerCase();
  const needsDelivery =
 status === "delivered" ||
 status === "partial";
  // const needsDelivery = curStatus === "delivered" || curStatus === "partial";

  if (needsDelivery) {
    if (!otpVerified)      { alert("OTP verification is mandatory for delivery", "err"); return; }
    if (!rcvName.trim())   { alert("Enter receiver name", "warn"); return; }
    if (!rcvAt)            { alert("Select Received At", "warn"); return; }
    if (!images.length)    { alert("Add at least one POD photo", "warn"); return; }
    if (!sigHasInk)        { alert("Receiver signature is required", "warn"); return; }
  } else {
    if (!rcvRemark.trim()) { alert("Enter an attempt / return remark", "warn"); return; }
  }
  setIsSubmitting(true);
  try {
    const { lat, lng } = await getGeoLocation();
    const fd = new FormData();
    fd.append("consignmentNo", curRecord.consignmentNo || curRecord.cnr);
    // fd.append("boyId",         curBoy);
    // fd.append("status",        curStatus.toUpperCase());
    fd.append("status", status.toUpperCase());
    fd.append("receiverName",  rcvName);
    fd.append("receivedAt",    rcvAt);
    fd.append("remark",        rcvRemark);
    // fd.append("otpVerified",   otpVerified);
    fd.append("otpId",         otpId || "");
    fd.append("isDirect",      isDirect);
    // createdBy is no longer sent from the client — the server resolves
    // it from the authenticated user's token (ResolveUserId()).
    fd.append("otpVerified",   otpVerified ? "true" : "false");
    fd.append("boyId",         parseInt(curBoy, 10));
    if (lat != null) fd.append("geoLat", lat);   // → drs.DeliveryUpdate.GeoLat & Track_Master.Latitude
    if (lng != null) fd.append("geoLng", lng);   // → drs.DeliveryUpdate.GeoLng & Track_Master.Longitude

    images.forEach((img) => {
      if (img.file) fd.append("images", img.file, img.name);
    });

    // Signature blob
    const sigBlob = await new Promise((res) =>
      sigCanvasRef.current?.toBlob(res, "image/png")
    );
    if (sigBlob) fd.append("signature", sigBlob, "signature.png");

    await api.post("/api/drs/update-delivery", fd, {
      headers: { "Content-Type": "multipart/form-data" },
    });

    const labels = {
      delivered: "Delivered", partial: "Partially delivered",
      attempted: "Attempt logged", returned: "Returned",
    };

    alert(`${curRecord.consignmentNo || curRecord.cnr} — ${labels[curStatus]}`, "ok");
    await loadConsignments();

    setTimeout(() => {
      setCurTab(curStatus === "attempted" || curStatus === "returned" ? "pending" : "history");
      backToList();
    }, 700);
  } catch (err) {
    alert(err.response?.data?.message || "Submit failed", "err");
    setIsSubmitting(false);
  }
};
  // ─────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────
  const needsDelivery = curStatus === "delivered" || curStatus === "partial";
  const boyObj = boys.find((b) => String(b.id || b.boyId) === String(curBoy));

  return (
    <div className="dru-root">
      <div className="dru-wrap">

        {/* ══ LIST VIEW ════════════════════════════════════════ */}
        {view === "list" && (
          <>

          {/* Boy selector */}
          {boys.length > 1 && (
            <div style={{ marginBottom: 10 }}>
              <select
                style={{
                  width: "100%", padding: "10px 12px", border: "1px solid var(--border)",
                  borderRadius: 8, fontFamily: "inherit", fontSize: 14,
                  fontWeight: 700, color: "var(--navy)", background: "#fff", outline: "none"
                }}
                value={curBoy}
                onChange={(e) => setCurBoy(e.target.value)}
              >
                {boys.map((b) => (
                  <option key={b.id || b.boyId} value={String(b.id || b.boyId)}>
                    🛵 {b.name || b.boyName} ({b.empCode})
                  </option>
                ))}
              </select>
            </div>
          )}

            {/* Stats */}
            {curTab !== "direct" && (
              <div className="stat-mini">
                <div className="sm-box amber">
                  <div className="sm-val">{pending.length}</div>
                  <div className="sm-lbl">Pending</div>
                </div>
                <div className="sm-box green">
                  <div className="sm-val">{delivered.length}</div>
                  <div className="sm-lbl">Delivered</div>
                </div>
                <div className="sm-box blue">
                  <div className="sm-val">{attempted.length}</div>
                  <div className="sm-lbl">Attempted</div>
                </div>
              </div>
            )}

            {/* Tabs */}
            <div className="dru-tabs">
              <button
                className={`dru-tab${curTab === "pending" ? " active" : ""}`}
                onClick={() => setCurTab("pending")}
              >
                <IconClipboard />
                Pending <span className="cnt-badge">{pending.length}</span>
              </button>
              <button
                className={`dru-tab${curTab === "history" ? " active" : ""}`}
                onClick={() => setCurTab("history")}
              >
                <IconClock />
                History <span className="cnt-badge">{history.length}</span>
              </button>
              <button
                className={`dru-tab${curTab === "direct" ? " active" : ""}`}
                onClick={() => { setCurTab("direct"); setDirectCnr(""); setDirectResult(null); setTimeout(() => directCnrRef.current?.focus(), 150); }}
              >
                <IconScan />
                Direct
              </button>
            </div>

            {/* ── DIRECT PANEL ── */}
            {curTab === "direct" && (
              <div className="dru-card">
                <div className="dru-band teal-band">
                  <div className="dru-band-icon"><IconScan /></div>
                  <div>
                    <div className="dru-band-title">Deliver Without DRS</div>
                    <div className="dru-band-sub">Enter a consignment number to deliver directly</div>
                  </div>
                </div>
                <div className="dru-card-body">
                  <div className="direct-scan">
                    <div className="direct-scan-wrap">
                      <span className="direct-scan-ico"><IconScan /></span>
                      <input
                        ref={directCnrRef}
                        className="direct-cnr-input"
                        type="text"
                        placeholder="Enter / scan consignment no"
                        autoComplete="off"
                        spellCheck={false}
                        value={directCnr}
                        onChange={(e) => setDirectCnr(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && lookupDirect()}
                      />
                    </div>
                    <button className="direct-go" onClick={lookupDirect}>
                      <IconArrow /> Fetch
                    </button>
                  </div>

                  {directResult && !directResult.found && (
                    <div className="direct-found" style={{ marginTop: 14 }}>
                      <div className="dru-card-body" style={{ textAlign: "center", color: "var(--red)" }}>
                        <div style={{ fontSize: 30 }}>🔍</div>
                        <div style={{ fontWeight: 700, marginTop: 6 }}>Consignment {directCnr} not found</div>
                        <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 3 }}>Check the number and try again</div>
                      </div>
                    </div>
                  )}

                  {directResult?.found && directResult.data && (() => {
                    const c = directResult.data;
                    return (
                      <div className="direct-found">
                        <div className="df-head">
                          <span className="df-cnr">{c.consignment_No || c.cnr}</span>
                          <span className="df-direct-tag">⚡ Direct · No DRS</span>
                        </div>
                        <div className="df-body">
                          {directResult.existing && (
                            <div className="df-row">
                              <span className="dfk">Status</span>
                              <span className="dfv" style={{ color: "var(--amber)" }}>
                                Already {directResult.existing.status} — re-updating overwrites
                              </span>
                            </div>
                          )}
                          <div className="df-row"><span className="dfk">Consignee</span><span className="dfv">{c.consignee_Name || c.consigneeName}</span></div>
                          <div className="df-row"><span className="dfk">Address</span><span className="dfv">{c.delivery_Place || c.deliveryPlace || "—"}</span></div>
                          <div className="df-row"><span className="dfk">Pieces</span><span className="dfv">{c.no_Pkgs || c.noPkgs || "—"} pcs</span></div>
                          <div className="df-row"><span className="dfk">Value</span><span className="dfv">₹ {fmt(c.charged_Weight || c.chargedWeight)}</span></div>
                        </div>
                        <div className="df-act">
                          <button
                            className="cn-update-btn"
                            style={{ width: "100%", justifyContent: "center" }}
                            onClick={() => openUpdate(String(c.consignment_No || c.cnr), true)}
                          >
                            <IconCheck /> Update Delivery
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </div>
            )}

            {/* ── PENDING TAB ── */}
            {curTab === "pending" && (
              pending.length === 0 ? (
                <div className="dru-card">
                  <div className="dru-empty">
                    <div className="dru-empty-ico">🎉</div>
                    <div className="dru-empty-title">All caught up</div>
                    <div className="dru-empty-sub">No pending deliveries for this delivery boy</div>
                  </div>
                </div>
              ) : pending.map((c, i) => (
                <div className="cn-card" key={i}>
                  <div className="cn-head">
                    <div className="cn-top">
                      <span className="cn-cnr">{c.consignmentNo || c.cnr}</span>
                      <span className="cn-area">📍 {c.area || c.deliveryPlace}</span>
                    </div>
                    <div className="cn-consignee">{c.consigneeName || c.consignee}</div>
                    <div className="cn-addr">{c.consigneeAddress || c.addr}</div>
                    <div className="cn-meta">
                      <span className="cn-tag">{c.pcs || c.no_Pkgs || "—"} pcs</span>
                      <span className="cn-tag">₹ {fmt(c.consignmentValue || c.val)}</span>
                    </div>
                  </div>
                  <div className="cn-foot">
                    {(c.mobile || c.consigneeMobile) && (
                      <a className="cn-call" href={`tel:${(c.mobile || c.consigneeMobile).replace(/\s/g, "")}`}>
                        <IconPhone /> Call
                      </a>
                    )}
                    <button
                      className="cn-update-btn"
                      onClick={() => openUpdate(String(c.consignmentNo || c.cnr))}
                    >
                      <IconCheck /> Update Delivery
                    </button>
                  </div>
                </div>
              ))
            )}

            {/* ── HISTORY TAB ── */}
            {curTab === "history" && (
              history.length === 0 ? (
                <div className="dru-card">
                  <div className="dru-empty">
                    <div className="dru-empty-ico">📭</div>
                    <div className="dru-empty-title">No history yet</div>
                    <div className="dru-empty-sub">Completed deliveries will appear here</div>
                  </div>
                </div>
              ) : history.map((c, i) => {
                const st  = (c.status || "").toLowerCase();
                const cfg = STATUS_CONFIG[st] || { label: st, icon: "•", cls: "" };
                return (
                  <div className="cn-card" key={i}>
                    <div className="cn-head">
                      <div className="cn-top">
                        <span className="cn-cnr">{c.consignmentNo || c.cnr}</span>
                        <span className={`cn-status ${cfg.cls}`}>{cfg.icon} {cfg.label}</span>
                      </div>
                      <div className="cn-consignee">{c.consigneeName || c.consignee}</div>
                      <div className="cn-addr">📍 {c.area || c.deliveryPlace}</div>
                      {c.receiver && (
                        <div className="cn-hist-info">
                          Received by <b>{c.receiver}</b>{c.receivedAt ? ` (${c.receivedAt})` : ""}
                        </div>
                      )}
                      {c.remark && (
                        <div className="cn-hist-info">Remark: <b>{c.remark}</b></div>
                      )}
                      {c.deliveredOn && (
                        <div className="cn-hist-info">{c.deliveredOn}{c.isDirect ? " · ⚡ Direct" : ""}</div>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </>
        )}

        {/* ══ UPDATE VIEW ══════════════════════════════════════ */}
        {view === "update" && curRecord && (
          <>
            <div style={{ margin: "2px 0 12px" }}>
              <button className="dru-btn dru-btn-ghost" style={{ padding: "8px 14px", fontSize: 13 }} onClick={backToList}>
                <IconBack /> Back
              </button>
            </div>

            {/* Banner */}
            <div className="uv-banner">
              <div className="uvb-top">
                <div className="uvb-cnr">{curRecord.consignment_No || curRecord.cnr}</div>
                {isDirect && <span className="uvb-direct">⚡ Direct · No DRS</span>}
              </div>
              <div className="uvb-consignee">{curRecord.consignee_Name || curRecord.consignee}</div>
              <div className="uvb-addr">{curRecord.consignee_Address || curRecord.addr}</div>
              <div className="uvb-meta">
                <div>
                  <div className="uvb-m-lbl">Area</div>
                  <div className="uvb-m-val">{curRecord.area || curRecord.delivery_Place || "—"}</div>
                </div>
                <div>
                  <div className="uvb-m-lbl">Pcs</div>
                  <div className="uvb-m-val">{curRecord.pcs || curRecord.no_Pkgs || "—"}</div>
                </div>
                <div>
                  <div className="uvb-m-lbl">Value</div>
                  <div className="uvb-m-val">₹{fmt(curRecord.consignment_Value || curRecord.cons_Value)}</div>
                </div>
                <div>
                  <div className="uvb-m-lbl">Mobile</div>
                  <div className="uvb-m-val">
                    {curRecord.mobile || curRecord.consignee_Mobile || "—"}
                  </div>
                </div>
              </div>
            </div>

            {/* ── STATUS PICKER ── */}
            <div className="dru-card">
              <div className="dru-band navy-band">
                <div className="dru-band-icon"><IconCheck /></div>
                <div>
                  <div className="dru-band-title">Delivery Status</div>
                  <div className="dru-band-sub">Select the outcome</div>
                </div>
              </div>
              <div className="dru-card-body">
                <div className="status-pick">
                  {Object.entries(STATUS_CONFIG).map(([key, cfg]) => (
                    <div
                      key={key}
                      className={`st-opt${curStatus === key ? " sel" : ""}`}
                      data-st={key}
                      onClick={() => setCurStatus(key)}
                    >
                      <span className="st-ico">{cfg.icon}</span>
                      {cfg.label}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── OTP (only for delivered/partial) ── */}
            {needsDelivery && (
              <div className="dru-card">
                <div className="dru-band amber-band">
                  <div className="dru-band-icon"><IconLock /></div>
                  <div>
                    <div className="dru-band-title">Delivery OTP</div>
                    <div className="dru-band-sub">Mandatory for delivery</div>
                  </div>
                </div>
                <div className="dru-card-body">
                  <div className="otp-info">
                    An OTP will be sent to the consignee at{" "}
                    <b>{curRecord.mobile || curRecord.consigneeMobile || "—"}</b>.
                    Verify it to confirm delivery.
                  </div>

                  {!otpSent && !otpVerified && (
                    <button className="otp-send-btn" onClick={() => sendOtp("SMS")}>
                      <IconSend /> Send OTP
                    </button>
                  )}

                  {otpSent && !otpVerified && (
                    <>
                      <div className="otp-inputs">
                        {otpDigits.map((d, i) => (
                          <input
                            key={i}
                            ref={otpRefs[i]}
                            className="otp-digit"
                            maxLength={1}
                            inputMode="numeric"
                            value={d}
                            onChange={(e) => handleOtpKey(e, i)}
                            onKeyDown={(e) => {
                              if (e.key === "Backspace" && !d && i > 0) {
                                otpRefs[i - 1].current?.focus();
                              }
                            }}
                          />
                        ))}
                      </div>
                      <button className="otp-verify-btn" onClick={verifyOtp}>Verify OTP</button>
                      <div className="otp-timer">
                        {otpResend
                          ? "Didn't get it? Resend via:"
                          : `Resend available in ${otpTimer}s`}
                      </div>
                      {/* {otpResend && (
                        <div className="otp-resend">
                          <button className="resend-btn sms" onClick={() => sendOtp("SMS")}>
                            <IconMsg /> SMS
                          </button>
                          <button className="resend-btn wa" onClick={() => sendOtp("WhatsApp")}>
                            <IconWa /> WhatsApp
                          </button>
                        </div>
                      )} */}
                      {otpResend && (
                        <div className="otp-resend">
                          {isDirect ? (
                            <>
                              <button className="resend-btn sms" onClick={() => sendOtp("SMS")}>
                                <IconMsg /> SMS
                              </button>
                              {/* <button className="resend-btn wa" onClick={() => sendOtp("WhatsApp")}>
                                <IconWa /> WhatsApp
                              </button> */}
                            </>
                          ) : (
                            <button className="resend-btn sms" onClick={resendOtp}>
                              <IconMsg /> Resend OTP
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {otpVerified && (
                    <div className="otp-verified show">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                      OTP Verified
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── RECEIVER DETAILS ── */}
            <div className="dru-card">
              <div className="dru-band teal-band">
                <div className="dru-band-icon"><IconUser /></div>
                <div>
                  <div className="dru-band-title">Receiver Details</div>
                  <div className="dru-band-sub">Who received the shipment</div>
                </div>
              </div>
              <div className="dru-card-body">
                <div className="dru-field">
                  <label>
                    Receiver Name{" "}
                    {needsDelivery && <span className="req">*</span>}
                  </label>
                  <input
                    type="text"
                    placeholder="Name of person who received"
                    value={rcvName}
                    onChange={(e) => setRcvName(e.target.value)}
                  />
                </div>
                <div className="dru-field">
                  <label>
                    Received At{" "}
                    {needsDelivery && <span className="req">*</span>}
                  </label>
                  <select value={rcvAt} onChange={(e) => setRcvAt(e.target.value)}>
                    <option value="">Select…</option>
                    {RECEIVED_AT_OPTIONS.map((o) => (
                      <option key={o}>{o}</option>
                    ))}
                  </select>
                </div>
                <div className="dru-field">
                  <label>
                    Remark{" "}
                    {!needsDelivery && <span className="req">*</span>}
                  </label>
                  <textarea
                    rows={2}
                    placeholder="Delivery / attempt remark…"
                    value={rcvRemark}
                    onChange={(e) => setRcvRemark(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* ── POD IMAGES ── */}
            <div className="dru-card">
              <div className="dru-band purple-band">
                <div className="dru-band-icon"><IconCamera /></div>
                <div>
                  <div className="dru-band-title">POD Images</div>
                  <div className="dru-band-sub">Photo proof of delivery</div>
                </div>
                <div className="dru-band-r">
                  <span className="dru-band-req">{images.length}</span>
                </div>
              </div>
              <div className="dru-card-body">
                <div className="photo-actions">
                  <button className="photo-btn" type="button" onClick={openCamera}>
                    <IconCamera />
                    <span>Take Photo</span>
                  </button>
                  <label className="photo-btn">
                    <IconImage />
                    <span>Gallery</span>
                    <input type="file" accept="image/*" multiple hidden onChange={addPhotos} />
                  </label>
                  <input
                    ref={nativeCamRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    hidden
                    onChange={addPhotos}
                  />
                </div>
                <div className="img-grid">
                  {images.length === 0 ? (
                    <div className="img-empty">No images yet — capture or upload POD photos</div>
                  ) : images.map((img, i) => (
                    <div className="img-thumb" key={i}>
                      <img src={img.url} alt={`POD ${i + 1}`} />
                      <button className="img-rm" onClick={() => removeImage(i)}>
                        <IconX />
                      </button>
                      <div className="img-num">POD {i + 1}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* ── SIGNATURE ── */}
            <div className="dru-card">
              <div className="dru-band green-band">
                <div className="dru-band-icon"><IconPen /></div>
                <div>
                  <div className="dru-band-title">Receiver Signature</div>
                  <div className="dru-band-sub">Sign with finger or stylus</div>
                </div>
              </div>
              <div className="dru-card-body">
                <div className="sig-wrap">
                  <canvas
                    ref={sigCanvasRef}
                    className="sig-canvas"
                    onPointerDown={sigStart}
                    onPointerMove={sigMove}
                    onPointerUp={sigEnd}
                    onPointerLeave={sigEnd}
                  />
                  <div className="sig-bar">
                    <span className="sig-hint">Sign above</span>
                    <button className="sig-clear" onClick={clearSig}>
                      <IconTrash /> Clear
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* ── SUBMIT BAR ── */}
            <div className="dru-submit-bar">
              <div className="dru-submit-bar-inner">
               {rights.add && (
                  <button
                    // className="submit-btn"
                    // onClick={submitDelivery}
                    // disabled={!curStatus}
                    // disabled={false}
                    className={`submit-btn${isSubmitting ? " submit-btn-loading" : ""}`}
                  onClick={submitDelivery}
                  disabled={!curStatus || isSubmitting}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Submit Delivery
                </button>)}
              </div>
            </div>
          </>
        )}

      </div>{/* /dru-wrap */}

      {/* ── CAMERA MODAL ── */}
      {camOpen && (
        <div className="cam-modal show">
          <div className="cam-hd">
            <span>Take POD Photo</span>
            <button className="cam-x" onClick={closeCamera}>✕</button>
          </div>
          <div className="cam-stage">
            <video ref={camVideoRef} playsInline autoPlay muted />
            <canvas ref={camCanvasRef} style={{ display: "none" }} />
            {camError && (
              <div className="cam-msg">
                <div className="cam-msg-ico">📷</div>
                <div>Camera not available or permission denied.<br />Use your phone's camera instead.</div>
                <button
                  style={{ padding: "10px 18px", border: "none", borderRadius: 8, background: "var(--teal)", color: "#fff", fontWeight: 700, fontFamily: "inherit", fontSize: 13, cursor: "pointer" }}
                  onClick={() => { closeCamera(); nativeCamRef.current?.click(); }}
                >
                  Open Phone Camera
                </button>
              </div>
            )}
          </div>
          <div className="cam-bar">
            <button className="cam-flip" onClick={flipCamera} aria-label="Switch camera">
              <IconFlip />
            </button>
            <button className="cam-shutter" onClick={capturePhoto} aria-label="Capture">
              <span />
            </button>
            <div style={{ width: 48 }} />
          </div>
        </div>
      )}

      <ToastWrap toasts={toasts} />
    </div>
  );
}